import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import axios from 'axios';
import { 
  Lock, Crown, Check, Sparkles, BookOpen, 
  Brain, Target, FileQuestion, PenTool, 
  ArrowLeft, ShoppingCart, Loader2, CheckCircle2,
  XCircle, RefreshCw, PartyPopper
} from 'lucide-react';

import { API_BASE } from "@/services/api";

// Payment Status Component
const PaymentStatusModal = ({ status, message, onClose, onRetry, optional_name, navigateTo }) => {
  const navigate = useNavigate();
  
  if (status === 'success') {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
        <Card className="max-w-md w-full animate-in slide-in-from-bottom sm:zoom-in-95 duration-300 rounded-t-2xl sm:rounded-2xl mx-0 sm:mx-4">
          <CardContent className="p-6 sm:p-8 text-center">
            <div className="w-16 sm:w-20 h-16 sm:h-20 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-10 sm:w-12 h-10 sm:h-12 text-green-600" />
            </div>
            <div className="flex justify-center gap-1 mb-4">
              <PartyPopper className="w-5 sm:w-6 h-5 sm:h-6 text-yellow-500" />
              <PartyPopper className="w-5 sm:w-6 h-5 sm:h-6 text-pink-500" />
              <PartyPopper className="w-5 sm:w-6 h-5 sm:h-6 text-blue-500" />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">Payment Successful!</h2>
            <p className="text-slate-600 mb-6 text-sm sm:text-base">{message}</p>
            <Button 
              onClick={() => navigate(navigateTo)}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Start Using {optional_name}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
        <Card className="max-w-md w-full animate-in slide-in-from-bottom sm:zoom-in-95 duration-300 rounded-t-2xl sm:rounded-2xl mx-0 sm:mx-4">
          <CardContent className="p-6 sm:p-8 text-center">
            <div className="w-16 sm:w-20 h-16 sm:h-20 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
              <XCircle className="w-10 sm:w-12 h-10 sm:h-12 text-red-600" />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">Payment Failed</h2>
            <p className="text-slate-600 mb-2 text-sm sm:text-base">{message}</p>
            <p className="text-xs sm:text-sm text-slate-500 mb-6">
              If any amount was debited, it will be automatically refunded within 3-5 business days.
            </p>
            <div className="flex gap-3">
              <Button 
                variant="outline"
                onClick={onClose}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={onRetry}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'verifying') {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
        <Card className="max-w-md w-full rounded-t-2xl sm:rounded-2xl mx-0 sm:mx-4">
          <CardContent className="p-6 sm:p-8 text-center">
            <Loader2 className="w-12 sm:w-16 h-12 sm:h-16 mx-auto mb-4 text-purple-600 animate-spin" />
            <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-2">Verifying Payment...</h2>
            <p className="text-slate-600 text-sm sm:text-base">Please wait while we confirm your payment.</p>
            <p className="text-xs sm:text-sm text-slate-500 mt-2">Do not close this window.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
};

const OptionalLockedPage = ({ lockedData, onBack }) => {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const { toast } = useToast();
  const [purchasing, setPurchasing] = useState(false);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // 'success', 'failed', 'verifying'
  const [statusMessage, setStatusMessage] = useState('');
  
  // Coupon code state
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  
  const {
    optional_name = 'Optional Subject',
    optional_key = 'optional',
    price = 1999,
    price_display = '₹1,999',
    original_price = 5999,
    original_price_display = '₹5,999',
    message = '🔒 This is a premium feature',
    description = 'Get complete access with all features:',
    features = [],
    cta_text = 'Buy Now'
  } = lockedData || {};

  const pathMap = {
    sociology: '/optional/sociology',
    pubadmin: '/optional/pub-admin',
    anthropology: '/optional/anthropology',
    geography: '/optional/geography',
    history: '/optional/history',
    psir: '/optional/psir'
  };

  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => setRazorpayLoaded(true);
    document.body.appendChild(script);
    
    return () => {
      // Cleanup if needed
    };
  }, []);
  
  // Validate coupon code
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast({ variant: "destructive", description: "Please enter a coupon code" });
      return;
    }
    
    setValidatingCoupon(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(`${API_BASE}/razorpay/validate-coupon`, {
        coupon_code: couponCode.trim(),
        product_id: `optional_${optional_key}`,
        optional_key: optional_key
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (data.valid) {
        setCouponApplied(data);
        toast({ description: `🎉 ${data.message} You save ${data.display_discount}!` });
      } else {
        toast({ variant: "destructive", description: data.message });
        setCouponApplied(null);
      }
    } catch (err) {
      toast({ variant: "destructive", description: err.response?.data?.detail || "Invalid coupon code" });
      setCouponApplied(null);
    } finally {
      setValidatingCoupon(false);
    }
  };
  
  // Remove coupon
  const handleRemoveCoupon = () => {
    setCouponCode('');
    setCouponApplied(null);
  };

  // Color themes for different optionals
  const themes = {
    sociology: {
      gradient: 'from-purple-600 to-indigo-700',
      light: 'bg-purple-50',
      border: 'border-purple-200',
      accent: 'text-purple-600',
      button: 'bg-purple-600 hover:bg-purple-700'
    },
    anthropology: {
      gradient: 'from-amber-600 to-orange-700',
      light: 'bg-amber-50',
      border: 'border-amber-200',
      accent: 'text-amber-600',
      button: 'bg-amber-600 hover:bg-amber-700'
    },
    pubadmin: {
      gradient: 'from-blue-600 to-cyan-700',
      light: 'bg-blue-50',
      border: 'border-blue-200',
      accent: 'text-blue-600',
      button: 'bg-blue-600 hover:bg-blue-700'
    }
  };

  const theme = themes[optional_key] || themes.sociology;

  const handlePurchase = async () => {
    if (!razorpayLoaded) {
      toast({
        variant: "destructive",
        title: "Payment Not Ready",
        description: "Please wait, payment system is loading..."
      });
      return;
    }

    setPurchasing(true);
    setPaymentStatus(null);
    
    try {
      // Get token from localStorage
      const authData = localStorage.getItem('ranker-auth');
      const token = authData ? JSON.parse(authData).token : null;
      
      if (!token) {
        toast({
          variant: "destructive",
          title: "Authentication Required",
          description: "Please login to purchase optional subjects."
        });
        navigate('/auth');
        return;
      }

      // Step 1: Create Razorpay order (with coupon if applied)
      const orderResponse = await axios.post(
        `${API_BASE}/razorpay/create-order`,
        { 
          product_id: `optional_${optional_key}`,
          optional_key: optional_key,
          coupon_code: couponApplied ? couponApplied.coupon_code : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const orderData = orderResponse.data;

      // 🎁 FREE-GRANT PATH (100% off coupon like BIRTHDAY100):
      // Backend already granted optional access — skip Razorpay checkout.
      if (orderData.free_grant && orderData.access_granted) {
        try {
          const meResponse = await axios.get(
            `${API_BASE}/auth/me`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (meResponse.data && user) {
            setUser({
              ...user,
              purchased_optionals: meResponse.data.purchased_optionals || []
            });
          }
        } catch (e) { /* non-fatal */ }
        toast({
          description: `🎂 ${orderData.coupon_applied} applied — 100% off! ${optional_name} unlocked.`,
          duration: 6000,
        });
        setPaymentStatus('success');
        setTimeout(() => { window.location.reload(); }, 1800);
        return;
      }

      // Step 2: Open Razorpay checkout
      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: "CLARIAS",
        description: `${optional_name} Optional Suite`,
        order_id: orderData.order_id,
        prefill: {
          email: orderData.user_email,
          name: orderData.user_name
        },
        notes: {
          product_id: orderData.product.id,
          optional_key: optional_key
        },
        theme: {
          color: "#7c3aed" // Purple theme
        },
        handler: async function (response) {
          // Step 3: Show verifying state
          setPaymentStatus('verifying');
          
          try {
            const verifyResponse = await axios.post(
              `${API_BASE}/razorpay/verify-payment`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );

            if (verifyResponse.data.status === 'success') {
              // Update user context with new purchased_optionals
              try {
                const meResponse = await axios.get(
                  `${API_BASE}/auth/me`,
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                
                if (meResponse.data && user) {
                  setUser({
                    ...user,
                    purchased_optionals: meResponse.data.purchased_optionals || []
                  });
                  
                  // Update localStorage
                  const storedAuth = localStorage.getItem('ranker-auth');
                  if (storedAuth) {
                    const parsed = JSON.parse(storedAuth);
                    parsed.user.purchased_optionals = meResponse.data.purchased_optionals || [];
                    localStorage.setItem('ranker-auth', JSON.stringify(parsed));
                  }
                }
              } catch (e) {
                console.error('Failed to refresh user data:', e);
              }

              // Show success modal
              setPaymentStatus('success');
              setStatusMessage(`You now have full access to ${optional_name} Paper 1 & Paper 2!`);
              
            } else if (verifyResponse.data.status === 'pending') {
              // Payment still processing
              toast({
                title: "Payment Processing",
                description: "Your payment is being processed. Access will be granted shortly."
              });
              setPaymentStatus(null);
            } else {
              throw new Error(verifyResponse.data.message || 'Verification failed');
            }
          } catch (verifyError) {
            console.error('Verification error:', verifyError);
            setPaymentStatus('failed');
            setStatusMessage(
              verifyError.response?.data?.detail || 
              "Payment verification failed. Please contact support@clarias.in with your payment ID."
            );
          }
          setPurchasing(false);
        },
        modal: {
          ondismiss: function() {
            setPurchasing(false);
            toast({
              title: "Payment Cancelled",
              description: "You can try again when ready."
            });
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      
      razorpay.on('payment.failed', function (response) {
        setPurchasing(false);
        setPaymentStatus('failed');
        setStatusMessage(
          response.error?.description || 
          "Payment failed. Please try again or use a different payment method."
        );
      });
      
      razorpay.open();

    } catch (error) {
      console.error('Purchase error:', error);
      setPurchasing(false);
      
      // Handle specific error cases
      if (error.response?.status === 400) {
        toast({
          variant: "destructive",
          title: "Already Purchased",
          description: error.response?.data?.detail || "You already have access to this optional."
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error Starting Payment",
          description: error.response?.data?.detail || "Something went wrong. Please try again."
        });
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 md:p-8">
      {/* Payment Status Modal */}
      <PaymentStatusModal 
        status={paymentStatus}
        message={statusMessage}
        optional_name={optional_name}
        navigateTo={pathMap[optional_key] || '/optional'}
        onClose={() => setPaymentStatus(null)}
        onRetry={() => {
          setPaymentStatus(null);
          handlePurchase();
        }}
      />
      
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={onBack || (() => navigate('/optional'))}
          className="mb-6 text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Optionals
        </Button>

        {/* Main Locked Card */}
        <Card className={`overflow-hidden border-2 ${theme.border} shadow-xl`}>
          {/* Header with Gradient */}
          <div className={`bg-gradient-to-r ${theme.gradient} p-8 text-white text-center`}>
            <div className="flex justify-center mb-4">
              <div className="bg-white/20 backdrop-blur-sm rounded-full p-4">
                <Lock className="w-12 h-12" />
              </div>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2">{optional_name} Optional</h1>
            <p className="text-white/90 text-lg">Premium UPSC Optional Subject Suite</p>
          </div>

          <CardContent className="p-8">
            {/* Price Section */}
            <div className="text-center mb-8">
              <Badge variant="outline" className={`${theme.accent} border-current mb-4 px-4 py-1 text-sm`}>
                <Sparkles className="w-4 h-4 mr-1" />
                One-Time Purchase • Valid till Mains 2027 (2 attempts)
              </Badge>
              <div className="flex items-center justify-center gap-3 mb-2">
                <span className="text-2xl text-slate-400 line-through">{original_price_display}</span>
                <span className="text-5xl font-bold text-slate-900">{price_display}</span>
                <Badge className="bg-green-500 text-white">40% OFF</Badge>
              </div>
              <p className="text-slate-500">Full access to Paper 1 & Paper 2 • Expires 30 Aug 2026</p>
            </div>

            {/* Features Grid */}
            <div className={`${theme.light} rounded-xl p-6 mb-8`}>
              <h3 className="font-semibold text-lg text-slate-800 mb-4 text-center">
                What&apos;s Included
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                {features.length > 0 ? (
                  features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3 bg-white rounded-lg p-3 shadow-sm">
                      <Check className={`w-5 h-5 ${theme.accent} flex-shrink-0 mt-0.5`} />
                      <span className="text-slate-700 text-sm">{feature.replace('✅ ', '')}</span>
                    </div>
                  ))
                ) : (
                  <>
                    <div className="flex items-start gap-3 bg-white rounded-lg p-3 shadow-sm">
                      <PenTool className={`w-5 h-5 ${theme.accent} flex-shrink-0`} />
                      <div>
                        <p className="font-medium text-slate-800">Answer Writing (ENGINE-1)</p>
                        <p className="text-xs text-slate-500">AI-powered top-ranker style answers</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 bg-white rounded-lg p-3 shadow-sm">
                      <Target className={`w-5 h-5 ${theme.accent} flex-shrink-0`} />
                      <div>
                        <p className="font-medium text-slate-800">Answer Evaluation (ENGINE-2)</p>
                        <p className="text-xs text-slate-500">Examiner-level feedback & scoring</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 bg-white rounded-lg p-3 shadow-sm">
                      <BookOpen className={`w-5 h-5 ${theme.accent} flex-shrink-0`} />
                      <div>
                        <p className="font-medium text-slate-800">PYQ Analysis (ENGINE-3)</p>
                        <p className="text-xs text-slate-500">30-year question pattern insights</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 bg-white rounded-lg p-3 shadow-sm">
                      <FileQuestion className={`w-5 h-5 ${theme.accent} flex-shrink-0`} />
                      <div>
                        <p className="font-medium text-slate-800">Expected Questions (ENGINE-4)</p>
                        <p className="text-xs text-slate-500">AI-predicted questions for 2025-26</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 bg-white rounded-lg p-3 shadow-sm">
                      <Brain className={`w-5 h-5 ${theme.accent} flex-shrink-0`} />
                      <div>
                        <p className="font-medium text-slate-800">Intelligent Diagrams</p>
                        <p className="text-xs text-slate-500">Auto-suggested exam-ready diagrams</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 bg-white rounded-lg p-3 shadow-sm">
                      <Crown className={`w-5 h-5 ${theme.accent} flex-shrink-0`} />
                      <div>
                        <p className="font-medium text-slate-800">Unlimited Access</p>
                        <p className="text-xs text-slate-500">No limits on generations</p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Papers Included */}
            <div className="flex justify-center gap-4 mb-8">
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${theme.light} ${theme.border} border`}>
                <Check className={`w-4 h-4 ${theme.accent}`} />
                <span className="text-sm font-medium text-slate-700">Paper 1</span>
              </div>
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${theme.light} ${theme.border} border`}>
                <Check className={`w-4 h-4 ${theme.accent}`} />
                <span className="text-sm font-medium text-slate-700">Paper 2</span>
              </div>
            </div>

            {/* Coupon Code Section */}
            <div className="max-w-md mx-auto mb-8 p-4 rounded-lg border border-slate-200 bg-slate-50">
              <h4 className="font-medium text-slate-900 mb-3 text-center">Have a coupon code?</h4>
              {!couponApplied ? (
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Enter coupon code"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm uppercase"
                    disabled={validatingCoupon}
                  />
                  <Button
                    onClick={handleApplyCoupon}
                    disabled={validatingCoupon || !couponCode.trim()}
                    variant="outline"
                    className="px-4"
                  >
                    {validatingCoupon ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-emerald-600" />
                      <span className="font-medium text-emerald-700">{couponApplied.coupon_code}</span>
                      <Badge className="bg-emerald-100 text-emerald-700 border-0">
                        {couponApplied.discount_percent}% OFF
                      </Badge>
                    </div>
                    <Button
                      onClick={handleRemoveCoupon}
                      variant="ghost"
                      size="sm"
                      className="text-slate-500 hover:text-red-500"
                    >
                      Remove
                    </Button>
                  </div>
                  <div className="text-sm space-y-1">
                    <div className="flex justify-between text-slate-600">
                      <span>Original Price:</span>
                      <span className="line-through">{couponApplied.display_original}</span>
                    </div>
                    <div className="flex justify-between text-emerald-600 font-medium">
                      <span>Discount:</span>
                      <span>-{couponApplied.display_discount}</span>
                    </div>
                    <div className="flex justify-between text-slate-900 font-bold text-base pt-1 border-t">
                      <span>Final Price:</span>
                      <span>{couponApplied.display_final}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* CTA Button */}
            <div className="text-center">
              <Button
                onClick={handlePurchase}
                disabled={purchasing || !razorpayLoaded}
                className={`${theme.button} text-white px-8 py-6 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50`}
              >
                {purchasing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : !razorpayLoaded ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Loading Payment...
                  </>
                ) : (
                  <>
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    {cta_text} - {couponApplied ? couponApplied.display_final : price_display}
                  </>
                )}
              </Button>
              <p className="text-xs text-slate-400 mt-4">
                🔒 Secure payment via Razorpay • <a href="/refund-policy" className="underline hover:text-slate-600">Refund Policy</a>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Trust Badges */}
        <div className="flex flex-wrap justify-center gap-6 mt-8 text-slate-500 text-sm">
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-green-500" />
            <span>Instant Access</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-green-500" />
            <span>Valid till 30 Aug 2027</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-green-500" />
            <span>All Updates Included</span>
          </div>
        </div>
        
        {/* Refund & Terms Notice */}
        <p className="text-center text-xs text-slate-400 mt-4">
          By purchasing, you agree to our <a href="/terms-of-service" className="underline hover:text-slate-600">Terms of Service</a> and <a href="/refund-policy" className="underline hover:text-slate-600">Refund Policy</a>
        </p>
      </div>
    </div>
  );
};

export default OptionalLockedPage;
